import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function Footer() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");

  const footerLinks = {
    [t("footer_col_product")]: [
      { label: t("footer_features"), href: "#producto", internal: false },
      { label: t("footer_pricing"), href: "#precios", internal: false },
      { label: t("footer_integrations"), href: "#integraciones", internal: false },
      { label: t("footer_roadmap"), href: "/roadmap", internal: true },
    ],
    [t("footer_col_company")]: [
      { label: t("footer_about"), href: "/sobre-nosotros", internal: true },
      { label: t("footer_blog"), href: "/blog", internal: true },
      { label: t("footer_use_cases"), href: "/casos-de-uso", internal: true },
      { label: t("footer_contact"), href: "mailto:auriareceptionistai@gmail.com", internal: false },
      { label: t("footer_manage_sub"), href: "/cuenta", internal: true },
    ],
    [t("footer_col_legal")]: [
      { label: t("footer_privacy"), href: "/privacidad", internal: true },
      { label: t("footer_terms"), href: "/terminos", internal: true },
      { label: t("footer_cookies"), href: "/privacidad#cookies", internal: true },
      { label: t("footer_gdpr"), href: "/privacidad#cookies", internal: true },
      { label: t("footer_sla"), href: "/terminos#sla", internal: true },
    ],
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) return;
    setStatus("sending");
    try {
      const formData = new URLSearchParams();
      formData.append("email", email);
      const res = await fetch("https://readdy.ai/api/form/d6u326hqcrg2vc5aq0gg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formData.toString(),
      });
      if (res.ok) {
        setStatus("success");
        setEmail("");
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  const handleNavClick = (href: string) => {
    if (href.startsWith("#") && href.length > 1) {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <footer style={{ background: "#0C0C0E" }} className="text-stone-400">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-16 pb-8">
        <div className="grid lg:grid-cols-5 gap-12 mb-12">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-5">
              <img
                src="https://storage.readdy-site.link/project_files/eabe49ed-6fb1-4abe-9d8b-950f8fdeaeed/20102fc6-5b65-4c43-8938-3a6df6c13095_21-mar-2026-02_01_49.png?v=96141c8a356bac035317d0f45aa9e568"
                alt="Auria Logo"
                className="h-9 w-auto brightness-0 invert"
              />
              <span className="text-xl font-bold text-white tracking-tight">Auria</span>
            </div>
            <p className="text-stone-400 text-sm leading-relaxed mb-6 max-w-xs">
              {t("footer_desc")}
            </p>

            <form onSubmit={handleSubmit} data-readdy-form className="space-y-3">
              <p className="text-white text-sm font-semibold">{t("footer_newsletter_title")}</p>
              <div className="flex gap-2">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("footer_newsletter_placeholder")}
                  className="flex-1 bg-white/5 border border-white/10 text-white placeholder-stone-600 text-sm rounded-xl px-4 py-2.5 outline-none focus:border-amber-500/50 transition-colors"
                  required
                />
                <button
                  type="submit"
                  disabled={status === "sending"}
                  className="px-4 py-2.5 bg-amber-500 hover:bg-amber-400 text-white text-sm font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  {status === "sending" ? (
                    <i className="ri-loader-4-line animate-spin" />
                  ) : (
                    <i className="ri-send-plane-line" />
                  )}
                </button>
              </div>
              {status === "success" && (
                <p className="text-emerald-400 text-xs flex items-center gap-1">
                  <i className="ri-check-circle-line" /> {t("footer_subscribe_success")}
                </p>
              )}
              {status === "error" && (
                <p className="text-red-400 text-xs flex items-center gap-1">
                  <i className="ri-error-warning-line" /> {t("footer_subscribe_error")}
                </p>
              )}
            </form>
          </div>

          {Object.entries(footerLinks).map(([title, links]) => (
            <div key={title}>
              <h4 className="text-white text-xs font-bold uppercase tracking-widest mb-5">{title}</h4>
              <ul className="space-y-3">
                {links.map((link) => (
                  <li key={link.label}>
                    {link.internal ? (
                      <Link
                        to={link.href}
                        className="text-sm text-stone-500 hover:text-white transition-colors cursor-pointer"
                      >
                        {link.label}
                      </Link>
                    ) : (
                      <a
                        href={link.href}
                        onClick={(e) => {
                          if (link.href.startsWith("#")) {
                            e.preventDefault();
                            handleNavClick(link.href);
                          }
                        }}
                        rel={
                          !link.href.startsWith("#") && !link.href.startsWith("mailto")
                            ? "nofollow"
                            : undefined
                        }
                        className="text-sm text-stone-500 hover:text-white transition-colors cursor-pointer"
                      >
                        {link.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t border-white/5 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-stone-600 text-xs">{t("footer_copyright")}</p>
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-stone-600 text-xs">{t("footer_status")}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
